import {
  Alert,
  Animated,
  //@ts-ignore
  AppState,
  BackHandler,
  Dimensions,
  Image,
  ImageBackground,
  NativeEventSubscription,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppIcons } from "../../assets/images";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import FastImage from "react-native-fast-image";
import Spacer from "../../components/global/Spacer";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  rollDice,
  moveTokenSteps,
  initGame,
  setDice,
  setCanRoll,
  checkPlayerWin,
  setCurrentPlayerIndex, // Add this action to your Redux slice
  PlayerColor,
  Token,
  LudoState,
  endTurn,
} from "../../redux/ludoSlice";
import {
  RedColorPath,
  BlueColorPath,
  GreenColorPath,
  YellowColorPath,
  Coordinate,
} from "../../utils/ludoPaths";
import LottieView from "lottie-react-native";
import {
  AdEventType,
  BannerAd,
  BannerAdSize,
  useForeground,
} from "react-native-google-mobile-ads";
import { bannerAdKey, interstitial } from "../../utils/contants";
import DeviceInfo from "react-native-device-info";
import SoundPlayer from "react-native-sound-player";
import {
  StackActions,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useAppSelector } from "../../redux/storeHook";
import { AppTheme } from "../../utils/theme";
import GameOverModal from "../../components/GameOverModal";
import { userAction } from "../../redux/reducers/user";
import { ROUTE_NAME } from "../../utils/routes";

const screenWidth =
  Platform.OS === "web"
    ? Math.min(Dimensions.get("window").width, 480)
    : Dimensions.get("window").width;
const BOARD_SIZE = Math.min(screenWidth * 0.95, 450); // Board takes 95% of screen width
const GRID_SIZE = 15; // 15x15 grid for Ludo board
const CELL_SIZE = BOARD_SIZE / GRID_SIZE; // Size of each cell
const HOME_FINISH_OFFSET = CELL_SIZE * 0.2;
const isTablet = DeviceInfo.isTablet();

// Piece images for each color
const PISES_IMAGES = {
  red: AppIcons.RedToken,
  blue: AppIcons.BlueToken,
  green: AppIcons.GreenToken,
  yellow: AppIcons.YellowToken,
};

// Path definitions for each player's tokens
const PATHS: Record<PlayerColor, Coordinate[]> = {
  red: RedColorPath,
  blue: BlueColorPath,
  green: GreenColorPath,
  yellow: YellowColorPath,
};

const TOKEN_SIZE = CELL_SIZE * 0.8;
const TOKEN_OFFSET = (CELL_SIZE - TOKEN_SIZE) / 2;
const STACK_OFFSET = CELL_SIZE * 0.15;

// Home turn coordinates
const HOME_TURN_COORDS: Record<PlayerColor, Coordinate> = {
  red: [7, 0],
  blue: [0, 7],
  yellow: [7, 14],
  green: [14, 7],
};

interface PixelPosition {
  x: number;
  y: number;
}

const cellToPixel = (row: number, col: number): PixelPosition => ({
  x: col * CELL_SIZE,
  y: row * CELL_SIZE,
});

const tokenAreaTopLeft: Record<PlayerColor, PixelPosition> = {
  red: { x: 0, y: 0 },
  blue: { x: BOARD_SIZE - CELL_SIZE * 6, y: 0 },
  green: { x: 0, y: BOARD_SIZE - CELL_SIZE * 6 },
  yellow: { x: BOARD_SIZE - CELL_SIZE * 6, y: BOARD_SIZE - CELL_SIZE * 6 },
};

const homePixelFor = (color: PlayerColor, tokenId: number): PixelPosition => {
  const areaSize = CELL_SIZE * 6;
  const area = tokenAreaTopLeft[color];
  const padding = (areaSize - 3 * TOKEN_SIZE) / 3;
  const col = tokenId % 2;
  const row = tokenId < 2 ? 0 : 1;
  const x = area.x + padding + col * (TOKEN_SIZE + padding);
  const y = area.y + padding + row * (TOKEN_SIZE + padding);
  // return { x: x - 2, y: y - 2 };
  return {
    x: isTablet
      ? tokenId == 0 || tokenId == 2
        ? x + 20
        : x + 10
      : tokenId == 0 || tokenId == 2
      ? x + 9
      : x + 5,
    y: tokenId == 2 || tokenId == 3 ? y + 3 : y + 8,
  };
};

const getFinishedOffset = (
  tokens: Record<PlayerColor, Token[]>,
  color: PlayerColor,
  tokenId: number
): { offX: number; offY: number } => {
  const finishedTokens = (tokens[color] || [])
    .filter((t) => t.status === "finished")
    .sort((a, b) => a.id - b.id);

  const numFinished = finishedTokens.length;

  const tokenIndex = finishedTokens.findIndex((t) => t.id === tokenId);

  if (tokenIndex === -1 || numFinished === 0) {
    return { offX: 0, offY: 0 };
  }

  const pOff = HOME_FINISH_OFFSET;

  let offX = 0;

  let offY = 0;

  switch (numFinished) {
    case 2:
      offX = tokenIndex === 0 ? -pOff : pOff;

      offY = 0;

      break;

    case 3:
      if (tokenIndex === 0) {
        offX = -pOff;

        offY = pOff * 0.5;
      } else if (tokenIndex === 1) {
        offX = pOff;

        offY = pOff * 0.5;
      } else {
        offX = 0;

        offY = -pOff * 1.5;
      }

      break;

    case 4:
      if (tokenIndex === 0) {
        offX = -pOff;

        offY = pOff;
      } else if (tokenIndex === 1) {
        offX = pOff;

        offY = pOff;
      } else if (tokenIndex === 2) {
        offX = -pOff;

        offY = -pOff;
      } else {
        offX = pOff;

        offY = -pOff;
      }

      break;
  }

  return { offX, offY };
};

interface TokenWithColor {
  color: PlayerColor;
  token: Token;
}

interface AppState {
  ludo: LudoState;
}

const getActivePlayers = (num: number): PlayerColor[] => {
  switch (num) {
    case 2:
      return ["red", "yellow"];
    case 3:
      return ["red", "blue", "yellow"];
    case 4:
      return ["red", "blue", "yellow", "green"];
    default:
      return ["red", "blue", "yellow", "green"];
  }
};

const PlayWithFriends = () => {
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const route = useRoute();
  const { params } = route as any;
  // Local state for number of players selection - default to null for selection screen
  //   const [numPlayers, setNumPlayers] = useState<number | null>(null);
  const [numPlayers, setNumPlayers] = useState<number | null>(
    params?.numPlayers || null
  );
  // Local state for game over and display winners
  const [gameOver, setGameOver] = useState(false);
  const [firstWinner, setFirstWinner] = useState<PlayerColor | null>(null);
  const [displayWinners, setDisplayWinners] = useState<PlayerColor[]>([]);
  const [isAnimatingDice, setIsAnimatingDice] = useState<boolean>(false);
  const { isSoundOn } = useAppSelector((state) => state.user);

  const lottieRef = useRef<LottieView>(null);
  const bannerRef = useRef<BannerAd>(null);
  // Redux hooks for state management
  const dispatch = useDispatch();
  const store = useStore();
  const state = useSelector((s: AppState) => s.ludo);
  let boomSub: NativeEventSubscription | null = null;
  let diceSub: NativeEventSubscription | null = null;
  let pieceSub: NativeEventSubscription | null = null;
  let pieceCutSub: NativeEventSubscription | null = null;

  let isLooping: boolean = false;
  let isDiceLooping: boolean = false;
  let isPieceLooping: boolean = false;
  let isPieceCutLooping: boolean = false;
  const [canTap, setCanTap] = useState<boolean>(true);
  // Destructure state values
  const {
    tokens, // All tokens by color
    paths, // Current paths for each player
    currentPlayerIndex, // Index of current player
    dice, // Current dice value
    winners, // Array of winners
    canRoll, // Whether player can roll dice
    capturedToken, // Whether a token was captured
    tokenReachedHome, // Whether a token reached home
    consecutiveSixes, // Count of consecutive sixes rolled
    isReturnInProgress, // Whether a return animation is in progress
  } = state;
  // Active players based on numPlayers
  const activePlayers = useMemo(
    () => (numPlayers ? getActivePlayers(numPlayers) : []),
    [numPlayers]
  );
  // Playing players excluding winners
  const playingPlayers = useMemo(
    () => activePlayers.filter((p) => !winners.includes(p)),
    [activePlayers, winners]
  );
  // Effective current color based on active players
  const effectiveCurrentIndex = currentPlayerIndex % activePlayers.length;
  const currentColor = activePlayers[effectiveCurrentIndex] || "red";
  // Compute all finished by color
  const allFinishedByColor = useMemo(() => {
    //@ts-ignore
    const finished: Record<PlayerColor, boolean> = {};
    activePlayers.forEach((color) => {
      finished[color] = (tokens[color] || []).every(
        (t) => t.status === "finished"
      );
    });
    return finished;
  }, [tokens, activePlayers]);
  // Animation references for token movements
  const anims = useRef<Record<PlayerColor, Record<number, Animated.ValueXY>>>({
    red: {},
    blue: {},
    green: {},
    yellow: {},
  }).current;
  // Helper to end turn correctly for active players only
  // Computes next effective index and overrides currentPlayerIndex after endTurn
  const endActiveTurn = useCallback(() => {
    if (!activePlayers.length) return;
    let currentEffective = currentPlayerIndex % activePlayers.length;
    let nextEffective = (currentEffective + 1) % activePlayers.length;
    // Skip over winners
    while (winners.includes(activePlayers[nextEffective])) {
      nextEffective = (nextEffective + 1) % activePlayers.length;
      if (nextEffective === currentEffective) {
        // All players won, end game
        setGameOver(true);
        return;
      }
    }
    dispatch(endTurn()); // Handles setCanRoll(true) and any slice side effects
    dispatch(setCurrentPlayerIndex(nextEffective)); // Override to align with active players
  }, [dispatch, currentPlayerIndex, activePlayers, winners, setGameOver]);

  /**
   * Handle game over logic when winners change
   */
  useEffect(() => {
    let finalWinners: PlayerColor[] = winners.filter((w) =>
      activePlayers.includes(w)
    );
    if (winners.length > 0 && firstWinner === null) {
      setFirstWinner(winners[0]);
    }
    if (playingPlayers.length === 1) {
      finalWinners = [...finalWinners, playingPlayers[0]];
      setGameOver(true);
    } else if (playingPlayers.length === 0) {
      setGameOver(true);
    }
    setDisplayWinners(finalWinners);
  }, [winners, activePlayers, playingPlayers, firstWinner]);

  // Handle return animation sound effects
  useEffect(() => {
    if (isReturnInProgress) {
      SoundPlayer.stop();
      setTimeout(() => {
        playPieceCutLoop();
      }, 500);
    }
    if (!isReturnInProgress) {
      stopPieceCutLoop();
      console.log("Return animation completed.");
    }
  }, [isReturnInProgress]);

  /**
   * Initialize animations when component mounts
   */
  useEffect(() => {
    if (numPlayers === null) return;
    activePlayers.forEach((color) => {
      if (!anims[color]) anims[color] = {};
      const path = paths[color] || PATHS[color] || [];
      // Initialize animation values for each token
      (tokens[color] || []).forEach((tok) => {
        if (!anims[color][tok.id]) {
          let pos: PixelPosition = { x: 0, y: 0 };
          // Set initial position based on token status
          if (tok.status === "onpath") {
            // Token is on the path - use path coordinates
            const coord = path[tok.pathIndex];
            pos = cellToPixel(coord[0], coord[1]);
          } else {
            // Token is home, returning, or finished
            if (tok.status === "home" || tok.status === "returning") {
              pos = homePixelFor(color, tok.id);
            } else if (tok.status === "finished") {
              const last = path[path.length - 1];
              // pos = cellToPixel(last[0], last[1]);
              let basePos = cellToPixel(last[0], last[1]);

              const { offX, offY } = getFinishedOffset(tokens, color, tok.id);

              basePos.x += offX;

              basePos.y += offY;

              pos = basePos;
            }
          }
          // Create animated value with offset to center token in cell
          anims[color][tok.id] = new Animated.ValueXY({
            x: pos.x + TOKEN_OFFSET,
            y: pos.y + TOKEN_OFFSET,
          });
        }
      });
    });
  }, [numPlayers]); // Run when numPlayers is set
  /**
   * Dice animation timer - rolls dice after 2000ms
   */
  useEffect(() => {
    if (isAnimatingDice) {
      const playDiceLoop = () => {
        if (isDiceLooping) return;
        isDiceLooping = true;
        if (isDiceLooping) {
          diceSub = SoundPlayer.addEventListener("FinishedPlaying", () => {
            if (isDiceLooping) {
              try {
                if (isSoundOn) {
                  SoundPlayer.playSoundFile("dise", "mp3");
                }
              } catch (error) {
                // Silently fail
              }
            }
          });
        }
        try {
          if (isSoundOn) {
            SoundPlayer.playSoundFile("dise", "mp3");
          }
        } catch (error) {
          // Silently fail on iOS if SoundPlayer is not available
          console.log("SoundPlayer not available on this platform");
        }
      };

      // Stop dice sound loop
      const stopDiceLoop = () => {
        isDiceLooping = false;
        if (diceSub) {
          diceSub.remove();
          diceSub = null;
        }
        try {
          SoundPlayer.stop();
        } catch (error) {
          // Silently fail
        }
      };

      playDiceLoop(); // Start dice sound loop

      if (lottieRef.current) {
        lottieRef.current.play(0, -1); // Play from start
      }
      const animTimer = setTimeout(() => {
        dispatch(rollDice() as any);
        setIsAnimatingDice(false);
        stopDiceLoop(); // Stop dice sound loop
        if (lottieRef.current) {
          lottieRef.current.reset(); // Reset for next play
        }
      }, 1000); // Match animation duration
      return () => clearTimeout(animTimer);
    }
  }, [isAnimatingDice]);

  useEffect(() => {
    if (gameOver && displayWinners.length > 0) {
      SoundPlayer.stop();
      setTimeout(() => {
        playLoop();
      }, 500);
    }
  }, [gameOver, displayWinners]);

  // Play boom sound loop
  const playLoop = () => {
    if (isLooping) return; // Already looping, avoid duplicates

    isLooping = true;

    if (isLooping) {
      boomSub = SoundPlayer.addEventListener("FinishedPlaying", () => {
        if (isLooping) {
          try {
            if (isSoundOn) {
              SoundPlayer.playSoundFile("boom", "mp3");
            }
          } catch (error) {
            // Silently fail
          }
        }
      });
    }
    try {
      if (isSoundOn) {
        SoundPlayer.playSoundFile("boom", "mp3");
      }
    } catch (error) {
      // Silently fail on iOS if SoundPlayer is not available
      console.log("SoundPlayer not available on this platform");
    }
  };

  // Stop boom sound loop
  const stopLoop = () => {
    isLooping = false;

    if (boomSub) {
      boomSub.remove(); // Remove listener
      boomSub = null;
    }
    try {
      SoundPlayer.stop(); // Stop sound immediately
    } catch (error) {
      // Silently fail
    }
  };

  // Play piece sound loop
  const playPieceLoop = () => {
    if (isPieceLooping) return;
    isPieceLooping = true;
    if (isPieceLooping) {
      pieceSub = SoundPlayer.addEventListener("FinishedPlaying", () => {
        if (isPieceLooping) {
          try {
            if (isSoundOn) {
              SoundPlayer.playSoundFile("pice", "mp3");
            }
          } catch (error) {
            // Silently fail
          }
        }
      });
    }
    try {
      if (isSoundOn) {
        SoundPlayer.playSoundFile("pice", "mp3");
      }
    } catch (error) {
      // Silently fail on iOS if SoundPlayer is not available
      console.log("SoundPlayer not available on this platform");
    }
  };

  // Stop piece sound loop
  const stopPieceLoop = () => {
    isPieceLooping = false;
    if (pieceSub) {
      pieceSub.remove();
      pieceSub = null;
    }
    try {
      SoundPlayer.stop();
    } catch (error) {
      // Silently fail
    }
  };

  // Play piece cut sound loop
  const playPieceCutLoop = () => {
    if (isPieceCutLooping) return;
    isPieceCutLooping = true;
    // if (isPieceCutLooping) {
    //   pieceCutSub = SoundPlayer.addEventListener("FinishedPlaying", () => {
    //     if (isPieceCutLooping) {
    //       try {
    //         SoundPlayer.playSoundFile("cutpice", "mp3");
    //       } catch (error) {
    //         // Silently fail
    //       }
    //     }
    //   });
    // }
    try {
      if (isSoundOn) {
        SoundPlayer.playSoundFile("cutpice", "mp3");
      }
    } catch (error) {
      // Silently fail on iOS if SoundPlayer is not available
      console.log("SoundPlayer not available on this platform");
    }
  };

  // Stop piece cut sound loop
  const stopPieceCutLoop = () => {
    isPieceCutLooping = false;
    if (pieceCutSub) {
      pieceCutSub.remove();
      pieceCutSub = null;
    }
    try {
      SoundPlayer.stop();
    } catch (error) {
      // Silently fail
    }
  };

  /** Animate token movements when tokens or paths change */
  useEffect(() => {
    if (numPlayers === null) return;
    activePlayers.forEach((color) => {
      const path = paths[color] || PATHS[color] || [];
      (tokens[color] || []).forEach((tok) => {
        const a = anims[color] && anims[color][tok.id];
        if (!a) return;
        let dest: PixelPosition = { x: 0, y: 0 };
        // Calculate destination based on token status
        if (tok.status === "onpath") {
          const coord = path[tok.pathIndex];
          dest = cellToPixel(coord[0], coord[1]);
        } else if (tok.status === "home") {
          dest = homePixelFor(color, tok.id);
        } else if (tok.status === "returning") {
          if (tok.pathIndex >= 0 && tok.pathIndex < path.length) {
            const coord = path[tok.pathIndex];
            dest = cellToPixel(coord[0], coord[1]);
          } else {
            dest = homePixelFor(color, tok.id);
          }
        } else if (tok.status === "finished") {
          const coord = path[path.length - 1];
          // dest = cellToPixel(coord[0], coord[1]);
          let baseDest = cellToPixel(coord[0], coord[1]);

          const { offX, offY } = getFinishedOffset(tokens, color, tok.id);

          baseDest.x += offX;

          baseDest.y += offY;

          dest = baseDest;
        }
        // Add offset to center token in cell
        dest = { x: dest.x + TOKEN_OFFSET, y: dest.y + TOKEN_OFFSET };
        // Animate token to new position
        Animated.timing(a, {
          toValue: { x: dest.x, y: dest.y },
          duration: 240, // Animation duration in milliseconds
          useNativeDriver: true, // Use native driver for better performance
        }).start();
      });
    });
  }, [tokens, paths, activePlayers]); // Run when tokens, paths, or activePlayers change
  /**
   * Calculate legal moves for a player based on dice value
   */
  const getLegalMoves = (color: PlayerColor, diceVal: number) => {
    const path = paths[color] || PATHS[color] || [];
    const myTokens = tokens[color] || [];
    const moves: { tokenId: number; action: "bringOut" | "move" }[] = [];
    const homeTurnCoord = HOME_TURN_COORDS[color];
    // Find the index where home stretch begins
    const homeTurnIndex = path.findIndex(
      (coord) => coord[0] === homeTurnCoord[0] && coord[1] === homeTurnCoord[1]
    );
    myTokens.forEach((t) => {
      if (t.status === "home") {
        // Token in home - can only come out with a 6
        if (diceVal === 6) moves.push({ tokenId: t.id, action: "bringOut" });
      } else if (t.status === "onpath") {
        const remaining = path.length - 1 - t.pathIndex;
        const isInHomeStretch =
          homeTurnIndex !== -1 && t.pathIndex >= homeTurnIndex;
        if (isInHomeStretch) {
          // In home stretch: can only move exact steps to finish
          if (diceVal <= remaining) {
            moves.push({ tokenId: t.id, action: "move" });
          }
        } else {
          // Not in home stretch: can move normally
          if (remaining > 0) {
            moves.push({ tokenId: t.id, action: "move" });
          }
        }
      }
    });
    return moves;
  };
  /**
   * Handle automatic moves when only one legal move is available
   */
  useEffect(() => {
    if (
      dice == null ||
      numPlayers === null ||
      gameOver ||
      playingPlayers.length <= 1 ||
      !playingPlayers.includes(currentColor)
    )
      return;
    const legal = getLegalMoves(currentColor, dice);
    // Check for three consecutive sixes rule (similar to Ludo King)
    if (dice === 6 && consecutiveSixes >= 3) {
      setTimeout(() => {
        dispatch(setDice(null));
        endActiveTurn();
      }, 400);
      return;
    }
    // No legal moves available
    if (legal.length === 0) {
      setTimeout(() => {
        dispatch(setDice(null));
        endActiveTurn();
      }, 1200);
      return;
    }
    // Only one legal move available - execute automatically
    if (legal.length === 1) {
      const only = legal[0];
      const moveSteps = only.action === "bringOut" ? "bringOut" : dice;
      playPieceLoop();
      dispatch(
        moveTokenSteps({
          color: currentColor,
          tokenId: only.tokenId,
          steps: moveSteps,
        }) as any
      ).then(() => {
        // Stop sound after animation duration to sync with token movement
        setTimeout(() => {
          stopPieceLoop();
        }, 240); // Match the token animation duration
        //@ts-ignore
        const stateAfterMove = store?.getState()?.ludo;
        const wasCaptured = stateAfterMove.capturedToken;
        dispatch(checkPlayerWin({ color: currentColor }));
        //@ts-ignore
        const stateAfterCheck = store?.getState()?.ludo;
        const reachedHome = stateAfterCheck.tokenReachedHome;
        const updatedWinners = stateAfterCheck.winners;
        const updatedPlayingPlayers = activePlayers.filter(
          (p) => !updatedWinners.includes(p)
        );
        dispatch(setDice(null));
        if (updatedPlayingPlayers.length === 0) {
          setGameOver(true);
          return;
        }
        // Check if current player has won - if so, force end turn without extra turn
        if (updatedWinners.includes(currentColor)) {
          endActiveTurn();
        } else {
          // Determine if player gets another turn
          if (wasCaptured || reachedHome || dice === 6) {
            dispatch(setCanRoll(true));
          } else {
            endActiveTurn();
          }
        }
      });
    }
  }, [
    dice,
    currentPlayerIndex,
    numPlayers,
    gameOver,
    endActiveTurn,
    playingPlayers,
    activePlayers,
  ]); // Run when dice value or current player changes

  useEffect(() => {
    if (!numPlayers) return;

    const onBackPress = () => {
      Alert.alert(
        "Exit Game",
        "Are you sure you want to exit the current game?",

        [
          { text: "Cancel", style: "cancel" },

          {
            text: "Exit",

            style: "destructive",

            onPress: () => {
              try {
                SoundPlayer.stop();
                restartGame();
              } catch (error) {
                // Silently fail
              }
            },
          },
        ]
      );

      return true; // Prevent default back action
    };

    const subscription = BackHandler.addEventListener(
      "hardwareBackPress",

      onBackPress
    );

    return () => subscription.remove();
  }, [numPlayers, navigation]);

  /**
   * useeffect for loading ads
   */

  useForeground(() => {
    Platform.OS === "ios" && bannerRef.current?.load();
  });

  // useEffect(() => {
  //   const unsubscribeLoaded = interstitial.addAdEventListener(
  //     AdEventType.LOADED,
  //     () => {
  //       console.log("@ interstitial ad loaded");
  //     }
  //   );

  //   const unsubscribeOpened = interstitial.addAdEventListener(
  //     AdEventType.OPENED,
  //     () => {
  //       console.log("@ interstitial ad opened");
  //     }
  //   );

  //   const unsubscribeClosed = interstitial.addAdEventListener(
  //     AdEventType.CLOSED,
  //     () => {
  //       console.log("@ interstitial ad closed");
  //     }
  //   );

  //   // Start loading the interstitial straight away
  //   // interstitial.load();

  //   // Unsubscribe from events on unmount
  //   return () => {
  //     unsubscribeLoaded();
  //     unsubscribeOpened();
  //     unsubscribeClosed();
  //   };
  // }, []);

  // Handle back action with confirmation
  const onBackCross = () => {
    Alert.alert(
      "Exit Game",
      "Are you sure you want to exit the current game?",

      [
        { text: "Cancel", style: "cancel" },

        {
          text: "Exit",

          style: "destructive",

          onPress: () => {
            try {
              backPress();
            } catch (error) {
              // Silently fail
            }
          },
        },
      ]
    );

    return true; // Prevent default back action
  };

  /**
   * Handle dice roll action
   */
  const onRoll = () => {
    if (
      gameOver ||
      playingPlayers.length <= 1 ||
      !playingPlayers.includes(currentColor)
    )
      return;
    if (isAnimatingDice) return; // Prevent multiple triggers
    setIsAnimatingDice(true);
  };
  /**
   * Handle token press - player selects which token to move
   */
  const onTokenPress = (color: PlayerColor, tokenId: number) => {
    if (
      gameOver ||
      playingPlayers.length <= 1 ||
      !playingPlayers.includes(currentColor) ||
      numPlayers === null ||
      color !== currentColor
    )
      return;
    if (dice == null) return;
    // Check for three consecutive sixes rule
    if (dice === 6 && consecutiveSixes >= 3) {
      dispatch(setDice(null));
      endActiveTurn();
      return;
    }
    const legal = getLegalMoves(color, dice);
    const chosen = legal.find((m) => m.tokenId === tokenId);
    if (!chosen) {
      if (legal.length === 0) {
        dispatch(setDice(null));
        endActiveTurn();
      } else {
      }
      return;
    }
    // Execute the chosen move
    const moveSteps = chosen.action === "bringOut" ? "bringOut" : dice;
    playPieceLoop();
    dispatch(moveTokenSteps({ color, tokenId, steps: moveSteps }) as any).then(
      () => {
        // Stop sound after animation duration to sync with token movement
        setTimeout(() => {
          stopPieceLoop();
        }, 240); // Match the token animation duration
        //@ts-ignore
        const stateAfterMove = store?.getState()?.ludo;
        const wasCaptured = stateAfterMove.capturedToken;
        dispatch(checkPlayerWin({ color }));
        //@ts-ignore
        const stateAfterCheck = store?.getState()?.ludo;
        const reachedHome = stateAfterCheck.tokenReachedHome;
        const updatedWinners = stateAfterCheck.winners;
        const updatedPlayingPlayers = activePlayers.filter(
          (p) => !updatedWinners.includes(p)
        );
        dispatch(setDice(null));
        if (updatedPlayingPlayers.length === 0) {
          setGameOver(true);
          return;
        }
        // Check if current player has won - if so, force end turn without extra turn
        if (updatedWinners.includes(color)) {
          endActiveTurn();
        } else {
          // Determine if player gets another turn
          if (wasCaptured || reachedHome || dice === 6) {
            dispatch(setCanRoll(true));
          } else {
            endActiveTurn();
          }
        }
      }
    );
  };
  /**
   * Get all tokens at a specific cell coordinate
   */
  const getTokensAtCell = (row: number, col: number): TokenWithColor[] => {
    const tokensAtCell: TokenWithColor[] = [];
    activePlayers.forEach((color) => {
      const path = paths[color] || PATHS[color] || [];
      (tokens[color] || []).forEach((token) => {
        // Only consider tokens that are on the path
        if (token.status === "onpath" || token.status === "returning") {
          if (token.pathIndex >= 0 && token.pathIndex < path.length) {
            const coord = path[token.pathIndex];
            if (coord && coord[0] === row && coord[1] === col) {
              tokensAtCell.push({ color, token });
            }
          }
        }
      });
    });
    return tokensAtCell;
  };
  /**
   * Calculate stacked position for tokens that occupy the same cell
   */
  const getStackedPosition = (
    color: PlayerColor,
    token: Token
  ): PixelPosition => {
    const path = paths[color] || PATHS[color] || [];
    // Only calculate for tokens on the path
    if (
      (token.status !== "onpath" && token.status !== "returning") ||
      token.pathIndex < 0 ||
      !path[token.pathIndex]
    ) {
      return { x: 0, y: 0 };
    }
    const coord = path[token.pathIndex];
    const tokensAtCell = getTokensAtCell(coord[0], coord[1]);
    // Sort tokens: current player's tokens on top, then by player order
    tokensAtCell.sort((a, b) => {
      if (a.color === currentColor && b.color !== currentColor) return 1;
      if (a.color !== currentColor && b.color === currentColor) return -1;
      if (a.color === b.color) return a.token.id - b.token.id;
      return activePlayers.indexOf(a.color) - activePlayers.indexOf(b.color);
    });
    // Find index of current token in the sorted array
    const tokenIndex = tokensAtCell.findIndex(
      (t) => t.color === color && t.token.id === token.id
    );
    const basePos = cellToPixel(coord[0], coord[1]);
    const offset = tokenIndex * STACK_OFFSET; // Calculate stacking offset
    return {
      x: basePos.x + TOKEN_OFFSET + offset,
      y: basePos.y + TOKEN_OFFSET + offset,
    };
  };

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextState) => {
      if (nextState === "background" && isSoundOn) {
        SoundPlayer.stop();
      }
    });
    return () => {
      subscription.remove();
    };
  }, [isSoundOn]);
  /**
   * Update token positions with stacking when multiple tokens share a cell
   */
  useEffect(() => {
    if (numPlayers === null) return;
    activePlayers.forEach((color) => {
      const path = paths[color] || PATHS[color] || [];
      (tokens[color] || []).forEach((tok) => {
        const a = anims[color] && anims[color][tok.id];
        if (!a) return;
        let dest: PixelPosition = { x: 0, y: 0 };
        if (tok.status === "onpath" || tok.status === "returning") {
          if (tok.pathIndex >= 0 && tok.pathIndex < path.length) {
            // Use stacked position for tokens on path
            dest = getStackedPosition(color, tok);
          } else {
            // Fallback to home position
            dest = homePixelFor(color, tok.id);
            dest = { x: dest.x + TOKEN_OFFSET, y: dest.y + TOKEN_OFFSET };
          }
        } else if (tok.status === "home") {
          dest = homePixelFor(color, tok.id);
          dest = { x: dest.x + TOKEN_OFFSET, y: dest.y + TOKEN_OFFSET };
        } else if (tok.status === "finished") {
          const coord = path[path.length - 1];
          dest = cellToPixel(coord[0], coord[1]);
          // dest = { x: dest.x + TOKEN_OFFSET, y: dest.y + TOKEN_OFFSET };
          let baseDest = cellToPixel(coord[0], coord[1]);

          const { offX, offY } = getFinishedOffset(tokens, color, tok.id);

          baseDest.x += offX;

          baseDest.y += offY;

          dest = { x: baseDest.x + TOKEN_OFFSET, y: baseDest.y + TOKEN_OFFSET };
        }
        // Animate to new position
        Animated.timing(a, {
          toValue: { x: dest.x, y: dest.y },
          duration: 240,
          useNativeDriver: true,
        }).start();
      });
    });
  }, [tokens, paths, activePlayers, currentPlayerIndex]); // Run when relevant state changes
  /**
   * Render a single cell with optional color
   */
  const renderCell = (color?: string, isColorPath: boolean = false) => (
    <View style={styles.cell} />
  );
  /**
   * Render the token pile in home areas
   */
  const renderTokensPile = () => (
    <View style={styles.tokensGrid}>
      {[0, 1, 2, 3].map((i) => (
        <View key={i}>
          <View />
        </View>
      ))}
    </View>
  );
  /**
   * Render dice image based on number
   */
  const getDiceImage = (num: any) => {
    console.log("Dice number:", num);
    switch (num) {
      case 1:
        return AppIcons.Dice_1;
      case 2:
        return AppIcons.Dice_2;
      case 3:
        return AppIcons.Dice_3;
      case 4:
        return AppIcons.Dice_4;
      case 5:
        return AppIcons.Dice_5;
      case 6:
        return AppIcons.Dice_6;
      default:
        return AppIcons.Dice_1; // fallback
    }
  };
  /**
   * Calculate z-index for proper token stacking order
   */
  const getTokenZIndex = (color: PlayerColor, token: Token): number => {
    // Non-path tokens have base z-index
    // if (token.status !== "onpath" && token.status !== "returning") return 10;
    if (token.status !== "onpath" && token.status !== "returning") {
      if (token.status === "finished") {
        const finishedTokens = (tokens[color] || [])
          .filter((t) => t.status === "finished")
          .sort((a, b) => a.id - b.id);

        const tokenIndex = finishedTokens.findIndex((t) => t.id === token.id);

        return 10 + (tokenIndex + 1); // Higher for later finished tokens
      }

      return 10;
    }
    const path = paths[color] || PATHS[color] || [];
    if (token.pathIndex < 0 || !path[token.pathIndex]) return 10;
    const coord = path[token.pathIndex];
    const tokensAtCell = getTokensAtCell(coord[0], coord[1]);
    // Sort tokens for proper stacking order
    tokensAtCell.sort((a, b) => {
      if (a.color === currentColor && b.color !== currentColor) return 1;
      if (a.color !== currentColor && b.color === currentColor) return -1;
      if (a.color === b.color) return a.token.id - b.token.id;
      return activePlayers.indexOf(a.color) - activePlayers.indexOf(b.color);
    });
    // Find token index for z-index calculation
    const tokenIndex = tokensAtCell.findIndex(
      (t) => t.color === color && t.token.id === token.id
    );
    return 10 + tokenIndex; // Higher z-index for tokens on top
  };
  /**
   * Calculate dice button position based on current player
   */
  const getDiceButtonStyle = () => {
    const size = CELL_SIZE * 1.2;
    const margin = isTablet ? 45 : 20;
    // Position dice button near current player's home area
    switch (currentColor) {
      case "red":
        return { top: -size - margin, left: 5 };
      case "blue":
        return { top: -size - margin, right: 5 };
      case "green":
        return { bottom: -size - margin, left: 5 };
      case "yellow":
        return { bottom: -size - margin, right: 5 };
      default:
        return { top: -size - margin, left: 5 };
    }
  };
  // Function to restart game (go back to player selection)
  const restartGame = () => {
    if (interstitial.loaded) {
      interstitial.show();
    }
    setGameOver(false);
    setFirstWinner(null);
    setDisplayWinners([]);
    setNumPlayers(null);
    dispatch(initGame());
    navigation.dispatch(
      StackActions.replace(ROUTE_NAME.SelectPlayer, { mode: "friends" })
    );
    SoundPlayer.stop();
    userAction.setMusicPreference(true);
    userAction.setPlayGame(false);

    stopLoop();
  };

  // Function to restart game (go back to player selection)
  const backPress = () => {
    SoundPlayer.stop();
    setGameOver(false);
    setFirstWinner(null);
    setDisplayWinners([]);
    setNumPlayers(null);
    dispatch(initGame());
    navigation.goBack();
    userAction.setMusicPreference(true);
    userAction.setPlayGame(false);
    stopLoop();
  };
  return (
    <ImageBackground source={AppIcons.BoardBackground} style={styles.mainView}>
      <TouchableOpacity
        style={styles.backButton}
        activeOpacity={0.7}
        onPress={() => {
          userAction.setTouchSound(true);
          onBackCross();
        }}
      >
        <Image
          source={AppIcons.BackIcon}
          resizeMode="contain"
          style={styles.backIcon}
        />
      </TouchableOpacity>
      <View style={{ flex: 1, justifyContent: "center" }}>
        <View style={styles.rowView}>
          {/* red token and dice */}
          <View style={styles.tokenView}>
            {activePlayers.includes("red") && (
              <FastImage
                source={AppIcons.TokenBackground}
                style={styles.tokenBack}
              >
                <Image source={AppIcons.RedToken} style={styles.token} />
              </FastImage>
            )}
            {currentColor === "red" && !gameOver && (
              <TouchableOpacity onPress={canRoll ? onRoll : undefined}>
                <FastImage
                  source={AppIcons.DiceBackground}
                  style={styles.diceBack}
                >
                  <View style={styles.diceview}>
                    {isAnimatingDice ? (
                      <LottieView
                        ref={lottieRef}
                        source={require("../../../src/assets/lottie/dice.json")}
                        loop={true} // Loop until timeout
                        style={[
                          styles.lottieDice,
                          { opacity: isAnimatingDice ? 1 : 0 },
                        ]}
                        speed={1} // Adjust if needed
                      />
                    ) : dice !== null ? (
                      <Image
                        source={getDiceImage(dice)}
                        style={{
                          width: isTablet ? 60 : 45,
                          height: isTablet ? 60 : 45,
                        }}
                      />
                    ) : (
                      <Image source={AppIcons.Dice} style={styles.dice} />
                    )}
                  </View>
                </FastImage>
              </TouchableOpacity>
            )}
          </View>
          {/* blue token and dice */}
          <View style={styles.tokenView}>
            {currentColor === "blue" && !gameOver && (
              <TouchableOpacity onPress={canRoll ? onRoll : undefined}>
                <FastImage
                  source={AppIcons.DiceBackground1}
                  style={[
                    styles.diceBack,
                    {
                      // transform: [{ rotate: "180deg" }],
                      marginRight: 5,
                    },
                  ]}
                >
                  <View style={styles.diceview1}>
                    {/* <Image source={AppIcons.Dice_3} style={styles.dice} /> */}
                    {isAnimatingDice ? (
                      <LottieView
                        ref={lottieRef}
                        source={require("../../../src/assets/lottie/dice.json")}
                        loop={true} // Loop until timeout
                        style={[
                          styles.lottieDice,
                          { opacity: isAnimatingDice ? 1 : 0 },
                        ]}
                        speed={1} // Adjust if needed
                      />
                    ) : dice !== null ? (
                      <Image
                        source={getDiceImage(dice)}
                        style={{
                          width: isTablet ? 60 : 45,
                          height: isTablet ? 60 : 45,
                        }}
                      />
                    ) : (
                      <Image source={AppIcons.Dice} style={styles.dice} />
                    )}
                  </View>
                </FastImage>
              </TouchableOpacity>
            )}
            {activePlayers.includes("blue") && (
              <FastImage
                source={AppIcons.TokenBackground}
                style={styles.tokenBack}
              >
                <Image source={AppIcons.BlueToken} style={styles.token} />
              </FastImage>
            )}
          </View>
        </View>
        <View style={styles.innerContainer}>
          {/* Game Board Container */}
          <View style={styles.boardContainer}>
            <View style={styles.board}>
              {/* Render 15x15 grid of cells */}
              {Array.from({ length: GRID_SIZE }).map((_, row) => (
                <View key={row} style={styles.row}>
                  {Array.from({ length: GRID_SIZE }).map((_, col) => {
                    return <View key={`${row}-${col}`}>{renderCell()}</View>;
                  })}
                </View>
              ))}
              <FastImage
                source={isTablet ? AppIcons.LudoBoard : AppIcons.LudoBoard}
                style={styles.boardImg}
              />
              {activePlayers.includes("red") && (
                <View style={[styles.tokenArea, { top: 0, left: 0 }]}>
                  {allFinishedByColor.red && firstWinner === "red" ? (
                    <Image
                      source={AppIcons.KingIcon} // Assuming AppIcons.KingIcon is added to your assets
                      style={styles.crown}
                      resizeMode="contain"
                    />
                  ) : (
                    renderTokensPile()
                  )}
                </View>
              )}
              {activePlayers.includes("blue") && (
                <View style={[styles.tokenArea, { top: 0, right: 0 }]}>
                  {allFinishedByColor.blue && firstWinner === "blue" ? (
                    <Image
                      source={AppIcons.KingIcon} // Assuming AppIcons.KingIcon is added to your assets
                      style={styles.crown}
                      resizeMode="contain"
                    />
                  ) : (
                    renderTokensPile()
                  )}
                </View>
              )}
              {activePlayers.includes("green") && (
                <View style={[styles.tokenArea, { bottom: 0, left: 0 }]}>
                  {allFinishedByColor.green && firstWinner === "green" ? (
                    <Image
                      source={AppIcons.KingIcon} // Assuming AppIcons.KingIcon is added to your assets
                      style={styles.crown}
                      resizeMode="contain"
                    />
                  ) : (
                    renderTokensPile()
                  )}
                </View>
              )}
              {activePlayers.includes("yellow") && (
                <View style={[styles.tokenArea, { bottom: 0, right: 0 }]}>
                  {allFinishedByColor.yellow && firstWinner === "yellow" ? (
                    <Image
                      source={AppIcons.KingIcon} // Assuming AppIcons.KingIcon is added to your assets
                      style={styles.crown}
                      resizeMode="contain"
                    />
                  ) : (
                    renderTokensPile()
                  )}
                </View>
              )}

              {activePlayers.map((color) => {
                //@ts-ignore
                return (tokens[color] || []).map((token) => {
                  const anim = anims[color] && anims[color][token.id];
                  if (!anim) return null;
                  const zIndex = getTokenZIndex(color, token);
                  return (
                    <TouchableWithoutFeedback
                      key={`${color}-${token.id}`}
                      onPress={() => {
                        setCanTap(false);
                        setTimeout(() => {
                          setCanTap(true);
                        }, 2000);
                        onTokenPress(color, token.id);
                      }}
                      disabled={!canTap}
                    >
                      <Animated.View
                        style={[
                          styles.liveToken,
                          {
                            zIndex,
                            transform: [
                              { translateX: anim.x },
                              { translateY: anim.y },
                            ],
                          },
                        ]}
                      >
                        <Image
                          source={PISES_IMAGES[color]}
                          style={styles.tokenImage}
                          resizeMode="contain"
                        />
                      </Animated.View>
                    </TouchableWithoutFeedback>
                  );
                });
              })}
            </View>
          </View>
        </View>
        <Spacer size={8} />
        <View style={styles.rowView}>
          {/* green token and dice */}
          <View style={styles.tokenView}>
            {activePlayers.includes("green") && (
              <FastImage
                source={AppIcons.TokenBackground}
                style={styles.tokenBack}
              >
                <Image source={AppIcons.GreenToken} style={styles.token} />
              </FastImage>
            )}

            {currentColor === "green" && !gameOver && (
              <TouchableOpacity onPress={canRoll ? onRoll : undefined}>
                <FastImage
                  source={AppIcons.DiceBackground}
                  style={styles.diceBack}
                >
                  <View style={styles.diceview}>
                    {isAnimatingDice ? (
                      <LottieView
                        ref={lottieRef}
                        source={require("../../../src/assets/lottie/dice.json")}
                        loop={true} // Loop until timeout
                        style={[
                          styles.lottieDice,
                          { opacity: isAnimatingDice ? 1 : 0 },
                        ]}
                        speed={1} // Adjust if needed
                      />
                    ) : dice !== null ? (
                      <Image
                        source={getDiceImage(dice)}
                        style={{
                          width: isTablet ? 60 : 45,
                          height: isTablet ? 60 : 45,
                        }}
                      />
                    ) : (
                      <Image source={AppIcons.Dice} style={styles.dice} />
                    )}
                  </View>
                </FastImage>
              </TouchableOpacity>
            )}
          </View>
          {/* yellow token and dice */}
          <View style={styles.tokenView}>
            {currentColor === "yellow" && !gameOver && (
              <TouchableOpacity onPress={canRoll ? onRoll : undefined}>
                <FastImage
                  source={AppIcons.DiceBackground1}
                  style={[
                    styles.diceBack,
                    {
                      // transform: [{ rotate: "180deg" }],
                      marginRight: 5,
                    },
                  ]}
                >
                  <View style={styles.diceview1}>
                    {isAnimatingDice ? (
                      <LottieView
                        ref={lottieRef}
                        source={require("../../../src/assets/lottie/dice.json")}
                        loop={true} // Loop until timeout
                        style={[
                          styles.lottieDice,
                          { opacity: isAnimatingDice ? 1 : 0 },
                        ]}
                        speed={1} // Adjust if needed
                      />
                    ) : dice !== null ? (
                      <Image
                        source={getDiceImage(dice)}
                        style={{
                          width: isTablet ? 60 : 45,
                          height: isTablet ? 60 : 45,
                        }}
                      />
                    ) : (
                      <Image source={AppIcons.Dice} style={styles.dice} />
                    )}
                  </View>
                </FastImage>
              </TouchableOpacity>
            )}
            {activePlayers.includes("yellow") && (
              <FastImage
                source={AppIcons.TokenBackground}
                style={styles.tokenBack}
              >
                <Image source={AppIcons.YellowToken} style={styles.token} />
              </FastImage>
            )}
          </View>
        </View>
        {displayWinners.length > 0 && gameOver && (
          <LottieView
            source={require("../../../src/assets/lottie/boom.json")}
            autoPlay={true}
            loop={true} // Loop until timer ends
            style={styles.lottieAnimation}
          />
        )}
        <View style={styles.botttomView}>
          <BannerAd
            ref={bannerRef}
            unitId={bannerAdKey}
            size={BannerAdSize.ANCHORED_ADAPTIVE_BANNER}
          />
        </View>
      </View>
      <GameOverModal
        visible={gameOver}
        winners={displayWinners}
        onClose={() => backPress()}
        onRestart={() => {
          setGameOver(false);
          setTimeout(() => {
            restartGame();
          }, 100);
        }}
      />
    </ImageBackground>
  );
};

export default PlayWithFriends;

const styles = StyleSheet.create({
  mainView: {
    flex: 1,
    backgroundColor: AppTheme.colors.boardBack,
  },
  backButton: {
    position: "absolute",
    top: Platform.OS === "web" ? 20 : 70,
    right: 0,
    width: 50,
    height: 40,
    zIndex: 99999,
    elevation: 999,
    cursor: "pointer",
  },
  backIcon: {
    width: 50,
    height: 40,
  },
  innerContainer: {
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  boardContainer: {
    position: "relative", // For absolute positioning of dice button
  },
  board: {
    width: BOARD_SIZE,
    height: BOARD_SIZE,
  },
  row: {
    flexDirection: "row",
  },
  cell: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    // borderWidth: 0.5,
  },
  boardImg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: CELL_SIZE * 15,
    height: CELL_SIZE * 15,
  },
  rowView: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
  },
  tokenBack: {
    width: 70,
    height: 70,
    paddingLeft: 18,
    paddingTop: 10,
  },
  token: {
    width: 25,
    height: 45,
  },
  tokenView: {
    flexDirection: "row",
    alignItems: "center",
  },
  diceBack: {
    width: 72,
    height: 60,
    marginBottom: 7,
  },
  dice: {
    width: 46,
    height: 50,
  },
  diceview: {
    width: "83%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-end",
  },
  diceview1: {
    width: "83%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
  },
  tokenArea: {
    position: "absolute",
    width: CELL_SIZE * 6,
    height: CELL_SIZE * 6,
    justifyContent: "center",
    alignItems: "center",
  },
  tokensGrid: {
    width: "70%",
    height: "70%",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignContent: "space-between",
  },
  crown: {
    width: "70%",
    height: "70%",
  },
  floatingDiceButton: {
    position: "absolute",
    zIndex: 1000,
  },
  lottieDice: {
    width: isTablet ? CELL_SIZE * 2.5 : CELL_SIZE * 2.8,
    height: isTablet ? CELL_SIZE * 2.5 : CELL_SIZE * 2.8,
    // marginTop: ,
  },
  controls: {
    marginTop: 20,
    alignItems: "center",
  },
  playerText: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 12,
  },
  buttonRow: {
    flexDirection: "row",
    marginTop: 8,
  },
  lottieAnimation: {
    height: 440,
    // width: 440,
    position: "absolute",
    top: -50,
    left: 0,
    right: 0,
    bottom: 0,
    margin: "auto",
  },
  liveToken: {
    position: "absolute",
    width: TOKEN_SIZE,
    height: TOKEN_SIZE,
    borderRadius: TOKEN_SIZE / 2,
    justifyContent: "center",
    alignItems: "center",
  },
  tokenImage: {
    width: isTablet ? 50 : 28, // Scale to fit within token container
    height: isTablet ? 50 : 28,
    marginBottom: 10,
  },
  botttomView: {
    position: "absolute",
    bottom: 10,
  },
  centerView: {
    position: "absolute",
    // zIndex: 9999,
    width: CELL_SIZE * 3,
    height: CELL_SIZE * 3,
    top: CELL_SIZE * 6,
    right: CELL_SIZE * 6,
  },
  backBtn: {
    position: "absolute",
    top: 70,
    left: 20,
  },
  backImg: {
    width: 40,
    height: 40,
  },
});
